import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useExplore, toCard, hasListingImage } from "@/lib/ExploreContext";
import CarouselRail from "@/components/yts/CarouselRail";
import ExperienceCard from "@/components/yts/ExperienceCard";
import RioPiedrasLiveMusic from "@/components/yts/rio/RioPiedrasLiveMusic";
import RioPiedrasStorySlideshow from "@/components/yts/rio/RioPiedrasStorySlideshow";
import RioPiedrasToday from "@/components/yts/rio/RioPiedrasToday";
import { useLang } from "@/lib/i18n";

const MAX_PLACES = 12;
const MAX_LIVE = 8;

// The barrio plays: these stages are what Río Piedras is known for, so they lead.
const LIVE_SUBS = /live music|salsa & dance|nightclubs/i;
const LIVE_WORDS = /\b(live music|música en vivo|salsa|bomba|plena|metal|alternative|alternativa|punk|indie|jazz|nightclub)\b/i;

// The order the guide tells the story in: the heavy room first, then the bomba
// floor, the theater and the indie club.
const LIVE_ORDER = ["el ocho de rio", "el boricua", "el ensayo", "club 77", "cafe boriken"];

const nameKey = (listing) => (listing.name || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

/**
 * Río Piedras as one consolidated block: its live-music lineup, the stages
 * themselves, then everything else the barrio has — bakeries, the market, the
 * bookshop, the museums and murals. Every card is read from the listings, so a
 * place added to the guide appears here on its own.
 */
export default function RioPiedrasRail() {
  const { lang } = useLang();
  const es = lang === "es";
  const { allListings, neighborhoods, nbNames } = useExplore();

  const { live, rest } = useMemo(() => {
    // The barrio is anchored by its neighborhood record; listings carry either
    // the slug or the record's id, so both answer.
    const rio = (neighborhoods || []).filter(
      (n) => n && (/r[ií]o\s*piedras/i.test(n.name || "") || n.slug === "rio-piedras")
    );
    const keys = new Set(["rio-piedras", ...rio.flatMap((n) => [n.id, n.slug]).filter(Boolean)]);
    const inRio = (l) => keys.has(l.neighborhood_id) || /r[ií]o piedras/i.test(l.address || "");

    const places = (allListings || []).filter(
      (l) => l && l.id && l.name && l.status !== "Archived" && inRio(l)
    );

    const plays = (l) =>
      LIVE_SUBS.test(l.subcategory || "") ||
      LIVE_WORDS.test(`${l.name} ${l.subcategory} ${l.amenities?.join(" ") || ""} ${l.description || ""}`);

    // A place you cannot walk into yet never takes a slot from one you can.
    const rank = (l) => (Number.isFinite(l.trending_rank) ? l.trending_rank : 99);
    const storyOrder = (l) => {
      const index = LIVE_ORDER.findIndex((key) => nameKey(l).startsWith(key));
      return index === -1 ? LIVE_ORDER.length + rank(l) : index;
    };

    const stages = places
      .filter((l) => !l.coming_soon && plays(l))
      .sort((a, b) => storyOrder(a) - storyOrder(b) || Number(hasListingImage(b)) - Number(hasListingImage(a)))
      .slice(0, MAX_LIVE);

    const stageIds = new Set(stages.map((l) => l.id));

    const others = places
      .filter((l) => !stageIds.has(l.id))
      .sort((a, b) => {
        const closed = Number(Boolean(a.coming_soon)) - Number(Boolean(b.coming_soon));
        if (closed) return closed;
        return rank(a) - rank(b) || Number(hasListingImage(b)) - Number(hasListingImage(a));
      })
      .slice(0, MAX_PLACES);

    const toCards = (list) => list.map((l) => toCard(l, nbNames)).filter((card) => card && card.id && card.title);

    return { live: toCards(stages), rest: toCards(others) };
  }, [allListings, neighborhoods, nbNames]);

  return (
    <div className="mt-6 sm:mt-9">
      <RioPiedrasToday />
      {live.length + rest.length > 0 && <div id="rio-local-places" className="mx-4 mt-7 scroll-mt-[120px] border-t border-[#d8d4cc] pt-4 sm:mx-6 lg:mx-auto lg:mt-9 lg:max-w-[1180px] dark:border-white/20">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="font-mono text-[9px] font-black uppercase tracking-[.18em] text-[#b34238]">{es ? "02 / EXPERIENCIAS Y LUGARES" : "02 / PLACES & EXPERIENCES"}</p>
            <h3 className="mt-1.5 text-[clamp(28px,6vw,48px)] font-black uppercase leading-[.92] tracking-[-.06em] text-[#171714] dark:text-[#f5f2eb]">{es ? "RÍO PIEDRAS SUENA." : "RÍO PIEDRAS PLAYS."}</h3>
          </div>
          <Link to="/RioPiedras" className="inline-flex min-h-10 items-center gap-1 border-b border-[#171714] text-[11px] font-extrabold text-[#171714] dark:border-white dark:text-[#f5f2eb]">{es ? "Ver guía completa" : "Full neighborhood guide"} <ArrowUpRight className="h-4 w-4"/></Link>
        </div>
        <p className="mt-2 max-w-[68ch] text-[12px] leading-[1.65] text-[#6d6d6a] dark:text-[#b8b1a6]">
          {es ? "Música en vivo, cultura universitaria, cafés y comercios independientes. Explora los lugares publicados de Río Piedras y sus propias historias." : "Live music, university culture, cafés and independent businesses. Explore real Río Piedras listings and their stories."}
        </p>
      </div>}

      {live.length + rest.length > 0 && <RioPiedrasLiveMusic venues={live} es={es} />}

      {/* Live venues already have dedicated mini cards above. Do not display
          the exact same establishments in a second carousel below. */}

      {rest.length >= 2 && (
        <CarouselRail
          masthead="RÍO PIEDRAS"
          title={es ? "El resto del barrio" : "The rest of the barrio"}
          subtitle={
            es
              ? "Panaderías, el mercado, la librería, los museos y los murales."
              : "Bakeries, the market, the bookshop, the museums and the murals."
          }
        >
          {rest.map((card) => <ExperienceCard key={card.id} item={card} />)}
        </CarouselRail>
      )}
      <div id="rio-history" className="scroll-mt-[120px] pt-3 sm:pt-5">
        <RioPiedrasStorySlideshow />
      </div>
    </div>
  );
}